import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Alert, Box } from '@mui/material';
import { SERVER_BASE_URL } from "./Config";

interface ProviderLoginProps {
  onLogin: (token: string, mustChangePassword: boolean) => void;
}

const ProviderLogin: React.FC<ProviderLoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState<string>('');
  const [forgotMessage, setForgotMessage] = useState<string>('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${SERVER_BASE_URL}/auth/provider`, { email, password });
      const { access_token, must_change_password } = response.data;
      onLogin(access_token, !!must_change_password);
      navigate(must_change_password ? '/change-password' : '/');
    } catch (err) {
      setError('Invalid provider credentials');
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const response = await axios.post(`${SERVER_BASE_URL}/auth/provider/forgot-password`, { email: forgotEmail });
      setForgotMessage(response.data.message);
    } catch (err) {
      setForgotMessage('An error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  if (showForgotPassword) {
    return (
      <Container maxWidth="xs" sx={{ mt: 12 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            Enter your email and we'll send you a temporary password.
          </Typography>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleForgotPassword(); }}
            sx={{ mb: 2 }}
          />
          {forgotMessage && <Alert severity="success" sx={{ mb: 2 }}>{forgotMessage}</Alert>}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleForgotPassword}
            disabled={forgotLoading}
          >
            {forgotLoading ? 'Sending...' : 'Send Reset Email'}
          </Button>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}>
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ mt: 12 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Provider Login
        </Typography>
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2 }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button variant="contained" fullWidth size="large" onClick={handleLogin}>
          Login
        </Button>
        <Box sx={{ mt: 1, textAlign: 'right' }}>
          <Button size="small" onClick={() => { setShowForgotPassword(true); setForgotEmail(email); }}>
            Forgot Password?
          </Button>
        </Box>
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Button variant="outlined" fullWidth onClick={() => navigate('/request-account')}>
            Request an Account
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProviderLogin;
